import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import { Add, Close, Remove } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { RootState } from "config/store";
import { useSnackbar } from "notistack";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getItemByEan } from "shared/network/item.api";
import { BaseCurrency } from "shared/types";

type Props = {
  scannerOpen: boolean;
  setScannerOpen: () => void;
  scannedData: string;
  setScannedData: Dispatch<SetStateAction<string>>;
  currency?: BaseCurrency;
  onSubmitForm: (item: any, selectedQuantity: number) => void;
};

type FormValues = {
  quantity: number;
};

const SellingItemByCodeHidden = ({
  scannerOpen,
  setScannerOpen,
  currency,
  onSubmitForm,
  scannedData,
  setScannedData,
}: Props) => {
  const { t } = useTranslation();
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const form = useForm<FormValues>();
  const tenant = useSelector((state: RootState) => state.authentication?.selectedRelTenant?.tenant);
  const [alertData, setAlertData] = useState<string | null>(null);

  const itemQuery = useQuery(
    ["itemByCodeQuery", scannedData, tenant?.id],
    async () => {
      if (scannerOpen && scannedData) {
        const { data } = await getItemByEan(tenant?.id, scannedData);
        return data?.item;
      }
      return Promise.reject();
    },
    { enabled: !!scannedData },
  );

  function saveAndContinue(values: FormValues) {
    if (itemQuery?.data) {
      onSubmitForm(itemQuery?.data, values.quantity);
      setScannedData("");
      form.setValue("quantity", 1);
    }
  }

  function onClose() {
    setScannerOpen();
    setScannedData("");
    setAlertData(null);
    form.reset({ quantity: 1 });
  }

  const handleQuantityChange = (delta: number) => {
    const current = Number(form.getValues("quantity") || 1);
    const next = Math.max(1, current + delta);
    form.setValue("quantity", next, { shouldValidate: true });
  };

  const handleQuantityKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      form.handleSubmit(saveAndContinue)();
    }
  };

  const handleReset = () => {
    setAlertData(null);
    setScannedData("");
    form.reset({ quantity: 1 });
  };

  useEffect(() => {
    if (scannerOpen && itemQuery.data && !itemQuery.isFetching) {
      setTimeout(() => {
        if (quantityInputRef.current) {
          quantityInputRef.current.focus();
          quantityInputRef.current.select();
        }
      }, 0);
    }
  }, [scannerOpen, itemQuery.data, itemQuery.isFetching]);

  //5997264104573

  return !!scannerOpen ? (
    <Dialog
      key={scannerOpen ? "open" : "closed"}
      fullWidth
      maxWidth="sm"
      open={!!scannerOpen}
      onClose={() => onClose()}
      PaperProps={{ style: { margin: 8, width: "100%" } }}
    >
      <DialogTitle style={{ paddingBottom: 4, padding: "16px 12px 0 12px" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>{t("qrCode.titleBar")}</span>
          <IconButton size="small" onClick={() => onClose()}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ padding: 12 }}>
        {alertData ? (
          <Box display="flex" flexDirection="column" gridGap={16}>
            <Alert severity="warning" variant="filled" style={{ borderRadius: 10 }}>
              {alertData}
            </Alert>
            <Button onClick={handleReset} style={{ marginBottom: 8 }}>
              {t("qrCode.newBarcode")}
            </Button>
          </Box>
        ) : scannedData ? (
          <Box>
            {!!itemQuery.error && (
              <Box display="flex" flexDirection="column" gridGap={16}>
                <Alert
                  severity="warning"
                  variant="filled"
                  style={{ borderRadius: 10, marginBottom: 8 }}
                >
                  {t("qrCode.notFound")}
                </Alert>
                <Button
                  onClick={() => {
                    handleReset();
                  }}
                  style={{ marginBottom: 8 }}
                >
                  {t("qrCode.newBarcode")}
                </Button>
              </Box>
            )}
            {!!itemQuery.isFetching ? (
              <Box display="flex" justifyContent="center" width="100%" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              !!itemQuery.data && (
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={12}>
                    <Typography style={{ fontWeight: "bold", fontSize: 18 }}>
                      {t("qrCode.item")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <Typography style={{ fontWeight: "bold" }}>{t("qrCode.name")}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <Typography>{itemQuery.data?.name}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <Typography style={{ fontWeight: "bold" }}>
                      {t("qrCode.productCode")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <Typography>{itemQuery.data?.productCode}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <Typography style={{ fontWeight: "bold" }}>
                      {t("warehouse.transaction.formValues.quantity")}:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <Box display="flex" alignItems="center">
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(-1)}
                        disabled={Number(form.getValues("quantity") || 1) <= 1}
                      >
                        <Remove />
                      </IconButton>
                      <Controller
                        control={form.control}
                        name={`quantity`}
                        defaultValue={1}
                        rules={{
                          required: t("validation.required").toString(),
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            inputRef={quantityInputRef}
                            type="number"
                            inputProps={{ step: "1", min: "1" }}
                            label={t("warehouse.transaction.formValues.quantity")}
                            InputLabelProps={{ shrink: true, required: true }}
                            style={{ maxWidth: 100, margin: "0 8px" }}
                            onKeyDown={handleQuantityKeyDown}
                          />
                        )}
                      />
                      <IconButton size="small" onClick={() => handleQuantityChange(1)}>
                        <Add />
                      </IconButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <Button
                      fullWidth
                      size="large"
                      variant="outlined"
                      onClick={form.handleSubmit(saveAndContinue)}
                      disabled={!itemQuery.data}
                    >
                      {t("qrCode.continue")}
                    </Button>
                  </Grid>
                </Grid>
              )
            )}
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height={200}
          >
            <Typography align="center" color="textSecondary" style={{ marginTop: 32 }}>
              {t("item.scanWithHardware")}
            </Typography>
            <Typography align="center" variant="caption" color="textSecondary">
              {t("qrCode.focusInfo", "A vonalkód olvasóval olvass be egy kódot!")}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  ) : null;
};

export default SellingItemByCodeHidden;
